import React, { useState, useCallback } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import TeamsListView from './TeamsListView';
import TeamDashboard from './TeamDashboard';
import TeamForm from './TeamForm';
import SprintForm from './SprintForm';

class CapacityErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[CapacityPlanner] Uncaught error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Something went wrong loading the Capacity Planner.{' '}
            <Button size="small" onClick={() => this.setState({ hasError: false })}>
              Try again
            </Button>
          </Alert>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * CapacityPlanner — view-state machine for the Capacity Planner tab in PMMode.
 *
 * Views:
 *   list          → TeamsListView
 *   dashboard     → TeamDashboard (selected team)
 *   team-form     → TeamForm (create or edit)
 *   sprint-form   → SprintForm (create or edit)
 */
export default function CapacityPlanner({ isReadOnly = false, onBackToHome }) {
  const [view, setView] = useState('list');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingSprintId, setEditingSprintId] = useState(null);

  // Team for sprint form context (to show member list)
  const [sprintTeam, setSprintTeam] = useState(null);

  // Filter state lifted here so it survives back-navigation remounts of TeamsListView
  const [listSearch, setListSearch] = useState('');
  const [listFilterOutcome, setListFilterOutcome] = useState(null);
  const [listFilterApplication, setListFilterApplication] = useState(null);

  const goList = useCallback(() => {
    setView('list');
    setSelectedTeamId(null);
    setEditingTeamId(null);
    setEditingSprintId(null);
    setSprintTeam(null);
  }, []);

  const goDashboard = useCallback((teamId) => {
    setSelectedTeamId(teamId);
    setView('dashboard');
  }, []);

  const goCreateTeam = useCallback(() => {
    setEditingTeamId(null);
    setView('team-form');
  }, []);

  const goEditTeam = useCallback((teamId) => {
    setEditingTeamId(teamId);
    setView('team-form');
  }, []);

  const goAddSprint = useCallback((teamId, team) => {
    setSelectedTeamId(teamId);
    setEditingSprintId(null);
    setSprintTeam(team || null);
    setView('sprint-form');
  }, []);

  const goEditSprint = useCallback((teamId, sprintId) => {
    setSelectedTeamId(teamId);
    setEditingSprintId(sprintId);
    setView('sprint-form');
  }, []);

  return (
    <CapacityErrorBoundary>
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Top-level header with back-to-home */}
      {onBackToHome && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, pt: 2, pb: 0 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBackToHome}>
            Home
          </Button>
          <Typography variant="subtitle1" fontWeight={600}>Capacity Planner</Typography>
        </Box>
      )}

      {view === 'list' && (
        <TeamsListView
          onSelectTeam={goDashboard}
          onCreateTeam={goCreateTeam}
          isReadOnly={isReadOnly}
          search={listSearch}
          onSearchChange={setListSearch}
          filterOutcome={listFilterOutcome}
          onFilterOutcomeChange={setListFilterOutcome}
          filterApplication={listFilterApplication}
          onFilterApplicationChange={setListFilterApplication}
        />
      )}

      {view === 'dashboard' && (
        <TeamDashboard
          teamId={selectedTeamId}
          onBack={goList}
          onEditTeam={goEditTeam}
          onAddSprint={(teamId) => goAddSprint(teamId, null)}
          onEditSprint={goEditSprint}
          isReadOnly={isReadOnly}
        />
      )}

      {view === 'team-form' && (
        <TeamForm
          teamId={editingTeamId}
          onSaved={(team) => {
            // After create/edit go to that team's dashboard
            goDashboard(team?.id || editingTeamId || selectedTeamId);
          }}
          onCancel={() => {
            // Go back to where we came from
            if (editingTeamId) {
              goDashboard(editingTeamId);
            } else {
              goList();
            }
          }}
          isReadOnly={isReadOnly}
        />
      )}

      {view === 'sprint-form' && (
        <SprintForm
          teamId={selectedTeamId}
          team={sprintTeam}
          sprintId={editingSprintId}
          onSaved={() => goDashboard(selectedTeamId)}
          onCancel={() => goDashboard(selectedTeamId)}
          isReadOnly={isReadOnly}
        />
      )}
    </Box>
    </CapacityErrorBoundary>
  );
}
